import { useEffect, useRef, useState } from "react";
import { Upload, Trash2, Eye, EyeOff, FileText, ImageIcon } from "lucide-react";
import { toast } from "sonner";
import api, { apiError, fileUrl, dateShort } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Pill } from "@/components/Bits";

const CATEGORIES = ["Renderings", "Moodboards", "Floor Plans", "Elevations", "Contracts", "Invoices",
  "Receipts", "Purchase Orders", "Vendor Documents", "Install Photos", "Client Documents", "Other"];

export default function FilesPanel({ projectId, spaceId, productId, vendorId, procurementId, compact, coverFileId, onCoverChange }) {
  const { isStaff } = useAuth();
  const [files, setFiles] = useState([]);
  const [category, setCategory] = useState("Renderings");
  const [filter, setFilter] = useState("");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef();

  const load = async () => {
    try {
      const { data } = await api.get("/files", {
        params: { project_id: projectId, space_id: spaceId, product_id: productId, vendor_id: vendorId },
      });
      setFiles(data);
    } catch {
      setFiles([]);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, spaceId, productId, vendorId]);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("category", category);
    if (projectId) fd.append("project_id", projectId);
    if (spaceId) fd.append("space_id", spaceId);
    if (productId) fd.append("product_id", productId);
    if (vendorId) fd.append("vendor_id", vendorId);
    if (procurementId) fd.append("procurement_id", procurementId);
    fd.append("visible_to_client", "false");
    try {
      await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("File uploaded");
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const toggleVisibility = async (f) => {
    try {
      await api.patch(`/files/${f.id}`, { visible_to_client: !f.visible_to_client });
      setFiles((prev) => prev.map((x) => (x.id === f.id ? { ...x, visible_to_client: !x.visible_to_client } : x)));
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const remove = async (f) => {
    try {
      await api.delete(`/files/${f.id}`);
      setFiles((prev) => prev.filter((x) => x.id !== f.id));
      toast.success("File removed");
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const setAsCover = async (f) => {
    try {
      const { data } = await api.put(`/projects/${projectId}/cover`, { file_id: f.id });
      setFiles((prev) => prev.map((x) => (x.id === f.id ? { ...x, visible_to_client: true } : x)));
      onCoverChange?.(data);
      toast.success("Cover image updated");
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const shown = filter ? files.filter((f) => f.category === filter) : files;

  return (
    <div className="space-y-6" data-testid="files-panel">
      {isStaff && (
        <div className="flex flex-wrap items-end gap-4">
          <label className="block">
            <span className="overline mb-2 block">Category</span>
            <select
              data-testid="file-category-select"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="rounded-sm border border-line bg-white px-3 py-2 text-sm outline-none focus:border-lime"
            >
              {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
            </select>
          </label>
          <label className="inline-flex cursor-pointer items-center gap-2 rounded-sm bg-lime px-4 py-2.5 text-sm font-medium text-ink transition-colors hover:bg-lime-dark">
            <Upload className="h-4 w-4" strokeWidth={1.5} />
            {busy ? "Uploading…" : "Upload File"}
            <input ref={fileRef} data-testid="file-upload-input" type="file" className="hidden" onChange={upload} disabled={busy} />
          </label>
          {!compact && (
            <select
              data-testid="file-filter-select"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="rounded-sm border border-line bg-white px-3 py-2 text-sm text-stone outline-none focus:border-lime"
            >
              <option value="">All categories</option>
              {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
            </select>
          )}
        </div>
      )}

      <div className="card-surface divide-y divide-line" data-testid="files-list">
        {shown.map((f) => {
          const isImage = (f.content_type || "").startsWith("image/");
          return (
            <div key={f.id} className="flex flex-wrap items-center gap-4 px-5 py-4">
              <a href={fileUrl(f.id)} target="_blank" rel="noreferrer" className="shrink-0">
                {isImage ? (
                  <img src={fileUrl(f.id)} alt={f.original_filename} className="h-12 w-16 rounded-sm object-cover bg-line" />
                ) : (
                  <div className="flex h-12 w-16 items-center justify-center rounded-sm border border-line bg-[#F1F0EC]">
                    <FileText className="h-4 w-4 text-fog" strokeWidth={1.5} />
                  </div>
                )}
              </a>
              <div className="min-w-0 flex-1">
                <a href={fileUrl(f.id)} target="_blank" rel="noreferrer" className="block truncate text-sm text-ink hover:text-lime-dark">
                  {f.original_filename}
                </a>
                <div className="mt-1 flex flex-wrap items-center gap-3 text-xs text-fog">
                  <Pill tone="bg-line text-stone">{f.category}</Pill>
                  {coverFileId === f.id && <Pill testId={`file-cover-badge-${f.id}`} tone="bg-lime text-ink">Cover Image</Pill>}
                  <span>{Math.round((f.size || 0) / 1024)} KB</span>
                  <span>{dateShort(f.created_at)}</span>
                  {f.project_name && !projectId && <span>{f.project_name}</span>}
                </div>
              </div>
              {isStaff && (
                <div className="flex items-center gap-2">
                  {isImage && projectId && onCoverChange && coverFileId !== f.id && (
                    <button
                      data-testid={`file-set-cover-${f.id}`}
                      onClick={() => setAsCover(f)}
                      title="Use this image as the project cover"
                      className="inline-flex items-center gap-2 rounded-sm border border-line bg-white px-3 py-1.5 text-xs text-stone transition-colors hover:border-lime hover:text-ink"
                    >
                      <ImageIcon className="h-3.5 w-3.5" strokeWidth={1.5} />
                      Set as Cover
                    </button>
                  )}
                  <button
                    data-testid={`file-visibility-toggle-${f.id}`}
                    onClick={() => toggleVisibility(f)}
                    title={f.visible_to_client ? "Visible to client" : "Internal only"}
                    className={`inline-flex items-center gap-2 rounded-sm border px-3 py-1.5 text-xs transition-colors ${
                      f.visible_to_client ? "border-lime bg-lime text-ink" : "border-line bg-white text-stone hover:border-fog"
                    }`}
                  >
                    {f.visible_to_client ? <Eye className="h-3.5 w-3.5" strokeWidth={1.5} /> : <EyeOff className="h-3.5 w-3.5" strokeWidth={1.5} />}
                    {f.visible_to_client ? "Client" : "Internal"}
                  </button>
                  <button
                    data-testid={`file-delete-${f.id}`}
                    onClick={() => remove(f)}
                    className="rounded-sm border border-line p-1.5 text-stone transition-colors hover:border-clay hover:text-clay"
                    aria-label="Delete file"
                  >
                    <Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} />
                  </button>
                </div>
              )}
            </div>
          );
        })}
        {shown.length === 0 && <div className="px-5 py-10 text-center text-sm text-fog">No files yet.</div>}
      </div>
    </div>
  );
}
